/**
 * What a right-click on a folded line offers: the flat row's menu, for every
 * conversation with that correspondent at once.
 *
 * The group is what the operator is looking at in this view, so it is what the
 * menu acts on. Filing a correspondent's six conversations used to mean
 * unfolding the line and right-clicking six rows; now it is one press, and the
 * rows stay available inside the fold for the one that needs different
 * treatment.
 *
 * Labels are deliberately not here. A label on one conversation is a decision
 * about that conversation, and stamping it on a whole group would label mail it
 * was never meant for.
 */
import { Archive, ArchiveRestore, Copy, Filter, Mail, MailOpen, ShieldAlert, ShieldCheck, Trash2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { BulkThreadPatch } from "../../../../lib/mailActions";
import { useCopyToast } from "../../../ui";
import { groupMenuState, summarizeMailGroup, type MailGroup } from "./group";

export function MailGroupItems({
  group,
  close,
  onOpen,
  onSearch,
  onPatchMany,
  onDeleteMany,
}: {
  group: MailGroup;
  close: () => void;
  onOpen: (id: string) => void;
  /** Put a term in the list's search box - "everything from this sender". */
  onSearch: (term: string) => void;
  onPatchMany: (ids: string[], patch: BulkThreadPatch) => Promise<void>;
  /** Asks first. The confirmation names the count, which is the whole point. */
  onDeleteMany: (group: MailGroup) => void;
}) {
  const t = useT();
  const copy = useCopyToast();
  const { who, latest } = summarizeMailGroup(group);
  const menu = groupMenuState(group);
  const count = String(group.rows.length);

  const act = (fn: () => void) => {
    close();
    fn();
  };

  return (
    <>
      <div className="crm-menu-head">
        <div className="crm-menu-t">{who}</div>
        <div className="crm-menu-s">{t("mail.group.count", { count })}</div>
      </div>

      <button type="button" className="crm-mi" onClick={() => act(() => onOpen(latest.id))}>
        <Mail className="size-4" />
        {t("mail.group.openLatest")}
      </button>

      {/* Like the row's own entry, this does not open anything, so no other
          operator sees these conversations counted as read by a click. */}
      <button
        type="button"
        className="crm-mi"
        onClick={() => act(() => void onPatchMany(menu.ids, { unread: !menu.anyUnread }))}
      >
        {menu.anyUnread ? <MailOpen className="size-4" /> : <Mail className="size-4" />}
        {t(menu.anyUnread ? "mail.group.markAllRead" : "mail.group.markAllUnread")}
      </button>

      <button
        type="button"
        className="crm-mi"
        onClick={() => act(() => void onPatchMany(menu.ids, { status: menu.allArchived ? "open" : "archived" }))}
      >
        {menu.allArchived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
        {t(menu.allArchived ? "mail.group.unarchiveAll" : "mail.group.archiveAll")}
      </button>

      <button
        type="button"
        className="crm-mi"
        onClick={() => act(() => void onPatchMany(menu.ids, { status: menu.allSpam ? "open" : "spam" }))}
      >
        {menu.allSpam ? <ShieldCheck className="size-4" /> : <ShieldAlert className="size-4" />}
        {t(menu.allSpam ? "mail.group.notSpamAll" : "mail.group.spamAll")}
      </button>

      {group.address ? (
        <>
          <button type="button" className="crm-mi" onClick={() => act(() => copy(group.address))}>
            <Copy className="size-4" />
            {t("email.row.copyAddress")}
          </button>
          <button type="button" className="crm-mi" onClick={() => act(() => onSearch(group.address))}>
            <Filter className="size-4" />
            {t("email.row.findSender")}
          </button>
        </>
      ) : null}

      <div className="crm-menu-sep" />
      <button type="button" className="crm-mi crm-mi-danger" onClick={() => act(() => onDeleteMany(group))}>
        <Trash2 className="size-4" />
        {t("mail.group.deleteAll", { count })}
      </button>
    </>
  );
}
